/**
 * My Stores persistence: API when authenticated (synced with mobile), localStorage fallback when not.
 */
import api from './api';

const SAVED_STORES_KEY = 'yona-saved-stores';
const MAX_SAVED = 20;

export const MY_STORES_UPDATED = 'yona-my-stores-updated';

function getStored() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SAVED_STORES_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setStored(list) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SAVED_STORES_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

function dispatchUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MY_STORES_UPDATED));
  }
}

export function storeId(store) {
  return store.id ?? `${store.name ?? ''}-${store.coordinates?.latitude}-${store.coordinates?.longitude}`;
}

/**
 * @returns {Promise<Array>} List of saved stores (from API when logged in, else localStorage).
 */
export async function getSavedStores() {
  try {
    const { data } = await api.get('/saved-stores/');
    return Array.isArray(data) ? data : [];
  } catch (err) {
    if (err?.response?.status === 401) {
      return getStored();
    }
    throw err;
  }
}

/**
 * Save a store (API when logged in, else localStorage). Dispatches MY_STORES_UPDATED.
 * @param {object} store Store object from toolkit search (name, coordinates, urls, etc.)
 * @returns {Promise<Array>} Updated list of saved stores (or current list from storage).
 */
export async function saveStore(store) {
  try {
    await api.post('/saved-stores/', store);
    dispatchUpdated();
    return getSavedStores();
  } catch (err) {
    if (err?.response?.status === 401) {
      const list = getStored();
      const id = storeId(store);
      const entry = { ...store, saved_at: Date.now() };
      const existing = list.findIndex((s) => storeId(s) === id);
      const next =
        existing >= 0
          ? (() => {
              const out = [...list];
              out[existing] = entry;
              return out;
            })()
          : [entry, ...list].slice(0, MAX_SAVED);
      setStored(next);
      dispatchUpdated();
      return next;
    }
    throw err;
  }
}

/**
 * Remove a saved store. id: backend pk (number) when using API, or store_id (string) for localStorage.
 * @param {number|string} id Backend primary key (number) or client store_id (string).
 * @returns {Promise<Array>} Updated list after remove.
 */
export async function removeSavedStore(id) {
  if (typeof id === 'number') {
    try {
      await api.delete(`/saved-stores/${id}/`);
      dispatchUpdated();
      return getSavedStores();
    } catch (err) {
      if (err?.response?.status === 401) {
        return removeSavedStoreLocal(id);
      }
      throw err;
    }
  }
  return removeSavedStoreLocal(id);
}

function removeSavedStoreLocal(storeIdValue) {
  const list = getStored().filter((s) => storeId(s) !== storeIdValue);
  setStored(list);
  dispatchUpdated();
  return Promise.resolve(list);
}
